/**
 * Expense module request config — the scopes + base URL for the accounting-api
 * expense PRODUCT routes (/v1/expense/*). Mirrors crm-config: every call mints a
 * platform-api-audience Entra token via the scope-parameterized acquireToken(scopes)
 * seam (the accounting-api validates the platform-api audience + expense.read/write
 * scopes — see AccountingSecurityConfig). NOT the parameterless acquireToken.
 *
 * Build-time env:
 *   - VITE_EXPENSE_SCOPES       — explicit platform-api expense scope(s); preferred.
 *   - else the configured scope union (single-registration dev default).
 *   - VITE_EXPENSE_API_BASE_URL — base for the accounting-api. Defaults same-origin
 *                                 '' (the edge serves /v1/expense/* on app.andavolabs.com).
 */
import { loadAuthConfig } from '@/auth/config';

function parseScopes(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The platform-api-audience scopes acquireToken() should mint a token for. */
export function getExpenseScopes(): string[] {
  const explicit = parseScopes(import.meta.env.VITE_EXPENSE_SCOPES);
  if (explicit.length > 0) return explicit;
  return loadAuthConfig().scopes;
}

/** Base for the accounting-api expense routes. Default same-origin (edge fan-out). */
export function getExpenseBaseUrl(): string {
  return import.meta.env.VITE_EXPENSE_API_BASE_URL ?? '';
}

/** Prefix a '/v1/expense/*' path with the configured base. */
export function expenseUrl(path: string): string {
  const base = getExpenseBaseUrl();
  if (!base) return path;
  return `${base.replace(/\/$/, '')}${path}`;
}
